import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  InputAdornment,
  Box,
  SxProps,
  Theme,
  Popper,
  Paper,
  Typography,
} from "@mui/material";
import CustomerCard from "../CustomerCard/CustomerCard";
import PolicyCard from "../PolicyCard/PolicyCard";
import PopperClaimCard from "../PopperClaimCard/PopperClaimCard";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import AgentSearchPopperStyles from "./AgentSearchPopperStyles";

interface ISearchPopperProps {
  id: string | undefined;
  open: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void | undefined;
  selectedOption: string;
}

const AgentSearchPopper: React.FC<ISearchPopperProps> = ({
  id,
  open,
  anchorEl,
  handleClose,
  selectedOption,
}) => {
  const popperRef = useRef<HTMLDivElement>(null);
  const { agentGlobalSearchData } = useAppSelector(
    (state: RootState) => state.globalSearch
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target as Node) &&
      anchorEl &&
      !anchorEl.contains(event.target as Node)
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorEl]);

  const filteredData = agentGlobalSearchData?.searchList
    ? agentGlobalSearchData.searchList?.filter((data: any) =>
        selectedOption.toLowerCase().includes(data.container.toLowerCase())
      )
    : [];

  return (
    <Popper
      id={id}
      data-test-id="agent-search-popper"
      aria-label="Search results popper"
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      className="w-[504px]"
      ref={popperRef}
      sx={AgentSearchPopperStyles.popperContainer}
    >
      <Paper 
        id="agent-search-paper"
        data-test-id="agent-search-paper"
        aria-label="Search results container"
        elevation={3} 
        sx={AgentSearchPopperStyles.paper}
      >
        <Box 
          id="agent-search-scroll-container"
          data-test-id="agent-search-scroll-container"
          aria-label="Scrollable search results"
          sx={AgentSearchPopperStyles.scrollContainer}
        >
          <Box
            id="agent-search-content"
            data-test-id="agent-search-content"
            aria-label="Search results content"
            sx={{
              p: 1,
            }}
          >
            {filteredData?.length > 0 ? (
              <>
                {filteredData.map((item: any, index: number) => {
                  if (
                    item.container === "Customer" ||
                    item.container === "Mobile"
                  ) {
                    return <CustomerCard item={item} />;
                  }
                  if (item.container === "Policy") {
                    return <PolicyCard item={item} />;
                  }
                  return <PopperClaimCard item={item} />;
                })}
                <Typography
                  id="agent-search-refine-text"
                  data-test-id="agent-search-refine-text"
                  aria-label="Search refinement message"
                  variant="body2"
                  sx={AgentSearchPopperStyles.refineText}
                >
                  {agentGlobalSearchData?.isMorethanTen
                    ? "More records found. Please refine your search."
                    : ""}
                </Typography>
              </>
            ) : (
              <Box 
                id="agent-search-no-records"
                data-test-id="agent-search-no-records"
                aria-label="No records found message"
                sx={AgentSearchPopperStyles.noRecordsContainer}
              >
                <Typography 
                  id="agent-search-no-records-text"
                  data-test-id="agent-search-no-records-text"
                  aria-label="No records found text"
                  sx={AgentSearchPopperStyles.noRecordsText}
                >
                  No Records Found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Popper>
  );
};

export default AgentSearchPopper;
